/**
 * Time Log Create Page — manual time entry with project selector.
 *
 * Fetches projects from BLL GET /v1/hros/projects, renders a select
 * alongside the standard schema-driven fields.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import { Button, Label } from '@yesbos/ui';
import { hrosApi } from '@/lib/api';

interface Project {
  id: string;
  name: string;
  status: string;
}

async function fetchProjects(): Promise<Project[]> {
  const res = await hrosApi.get<Project[]>('/v1/hros/projects?limit=100');
  if (res.success && Array.isArray(res.data)) {
    return res.data.filter((p) => p.status === 'active');
  }
  return [];
}

async function createTimeLog(payload: Record<string, unknown>): Promise<string> {
  const res = await hrosApi.post<{ id: string }>('/v1/hros/time-logs', payload);
  if (!res.success) throw new Error(res.error?.message ?? 'Failed to create time log');
  return res.data?.id ?? '';
}

export function TimeTrackerCreatePage() {
  const navigate = useNavigate();
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Form state
  const [projectId, setProjectId] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [hours, setHours] = React.useState('');

  React.useEffect(() => {
    fetchProjects()
      .then(setProjects)
      .catch(() => setError('Failed to load projects. You can still log time without selecting a project.'));
  }, []);

  // Auto-calculate hours when both times are set
  React.useEffect(() => {
    if (startTime && endTime) {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diff = (end.getTime() - start.getTime()) / (1000 * 3600);
      if (diff > 0) setHours(diff.toFixed(2));
    }
  }, [startTime, endTime]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        description: description || undefined,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        hours: hours ? parseFloat(hours) : undefined,
        project_id: projectId || undefined,
      };
      const id = await createTimeLog(payload);
      navigate(id ? `/time-tracker/${id}` : '/time-tracker');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Log Time</h1>
        <Button variant="ghost" onClick={() => navigate('/time-tracker')}>Cancel</Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Project selector */}
        <div className="space-y-1.5">
          <Label htmlFor="project_id">Project</Label>
          <select
            id="project_id"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">— No project —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="description">Description</Label>
          <input
            id="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What did you work on?"
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Start / End time */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="start_time">Start Time</Label>
            <input
              id="start_time"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="end_time">End Time</Label>
            <input
              id="end_time"
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Hours (manual or auto-computed) */}
        <div className="space-y-1.5">
          <Label htmlFor="hours">
            Hours
            {startTime && endTime && hours && (
              <span className="ml-2 text-xs text-muted-foreground">(auto-calculated)</span>
            )}
          </Label>
          <input
            id="hours"
            type="number"
            step="0.25"
            min="0"
            max="24"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="e.g. 2.5"
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving…' : 'Save Time Log'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/time-tracker')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default TimeTrackerCreatePage;
